import { autoLayoutNodes } from '../whatsapp-chatbot/chatbotGraph';

export const AUTOMATION_JSON_VERSION = '1.0.0';
export const NODE_TYPE_TRIGGER = 'trigger';
export const NODE_TYPE_ACTION = 'action';
export const NODE_TYPE_STOP = 'stop';
export const HANDLE_OUT = 'out';

const ACTION_ID_PREFIX = 'act';
const STOP_ID_PREFIX = 'stp';

function parseBotJson(value) {
  if (!value) return {};
  let parsed = value;
  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value);
    } catch (e) {
      return {};
    }
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
  return parsed;
}

function nextPrefixedId(items, prefix) {
  let max = 0;
  const re = new RegExp('^' + prefix + '(\\d+)$');
  (items || []).forEach(item => {
    const m = String((item && item.id) || '').match(re);
    if (m) {
      const n = parseInt(m[1], 10);
      if (n > max) max = n;
    }
  });
  return prefix + (max + 1);
}

/**
 * Action node ids are also the action row ids on the BE, so they must never be reused
 * (a deleted-then-re-added `act2` would take over the old row's id and scheduleActionId links).
 */
function uniqueNodeId(nodes, prefix) {
  const taken = {};
  (nodes || []).forEach(node => { if (node && node.id) taken[node.id] = true; });
  let id;
  do {
    id = prefix + '_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
  } while (taken[id]);
  return id;
}

function opValue(value) {
  if (value === 'OR') return 'OR';
  return 'AND';
}

export function rowOrder(index) {
  return String((index + 1) * 10);
}

export function withRowOrders(rows) {
  return (Array.isArray(rows) ? rows : []).map((row, index) => {
    if (!row || typeof row !== 'object' || Array.isArray(row)) return row;
    return { ...row, order: rowOrder(index) };
  });
}

function copyRows(rows) {
  return (Array.isArray(rows) ? rows : []).filter(row => row && typeof row === 'object' && !Array.isArray(row)).map(row => ({ ...row }));
}

function withTriggerFiltersOp(nodes, rule) {
  let updated = false;
  return (nodes || []).map(node => {
    if (updated || !node || node.type !== NODE_TYPE_TRIGGER) return node;
    updated = true;
    const data = { ...(node.data || {}) };
    if (data.filtersOp === 'AND' || data.filtersOp === 'OR') return node;
    return {
      ...node,
      data: { ...data, filtersOp: opValue(rule && rule.filtersOp) },
    };
  });
}

function sanitizeNode(node, existingNodes) {
  if (!node || typeof node !== 'object' || Array.isArray(node)) return null;
  const next = { ...node };
  delete next.isStartNode;
  if (!next.id) next.id = nextPrefixedId(existingNodes, 'n');
  if (next.data && typeof next.data === 'object' && !Array.isArray(next.data)) {
    next.data = { ...next.data };
    delete next.data.isStartNode;
  }
  const position = next.position && typeof next.position === 'object' ? next.position : {};
  next.position = {
    ...position,
    posX: position.posX != null ? position.posX : 0,
    posY: position.posY != null ? position.posY : 0,
  };
  return next;
}

function sanitizeNodes(nodes) {
  const list = [];
  (Array.isArray(nodes) ? nodes : []).forEach(node => {
    const next = sanitizeNode(node, list);
    if (next) list.push(next);
  });
  return list;
}

export function automationFromRule(rule) {
  const rec = rule || {};
  const hasBotJson = rec.botJson != null && rec.botJson !== '';
  let version = AUTOMATION_JSON_VERSION;
  let nodes = [];
  let edges = [];
  if (hasBotJson) {
    const parsed = parseBotJson(rec.botJson);
    version = parsed.version || AUTOMATION_JSON_VERSION;
    nodes = sanitizeNodes(parsed.nodes);
    edges = sanitizeEdges(parsed.edges);
  }
  // The BE always returns the full graph built from the rule's primary fields. Without a trigger
  // node the flow cannot be trusted: saving it would replace the rule's filters / actions / stops.
  if (!nodes.some(node => node && node.type === NODE_TYPE_TRIGGER)) {
    return { version, nodes, edges, incomplete: true };
  }
  nodes = withTriggerFiltersOp(nodes, rec);
  return { version, nodes, edges };
}

export function botJsonFromAutomation(graph) {
  const g = graph || {};
  const nodes = (Array.isArray(g.nodes) ? g.nodes : []).map(node => {
    if (!node || typeof node !== 'object') return null;
    const next = { ...node };
    delete next.isStartNode;
    if (next.data && typeof next.data === 'object' && !Array.isArray(next.data)) {
      const data = { ...next.data };
      delete data.isStartNode;
      delete data.eventLabel;
      delete data.filterLines;
      delete data.actionLabel;
      delete data.detailLine;
      delete data.scheduleLine;
      delete data.headline;
      delete data.lines;
      delete data.onDelete;
      delete data.onDuplicate;
      next.data = data;
    }
    return next;
  }).filter(Boolean);
  return {
    version: g.version || AUTOMATION_JSON_VERSION,
    nodes,
    edges: persistEdges(g.edges),
  };
}

export function toFlowNodes(nodes, previewForNode) {
  return (nodes || []).map(node => {
    if (!node) return null;
    const data = { ...(node.data || {}) };
    if (previewForNode) {
      const extra = previewForNode(node);
      if (extra) Object.assign(data, extra);
    }
    const position = node && node.position ? node.position : {};
    return {
      id: node.id,
      type: node.type,
      position: {
        x: position.posX != null ? position.posX : 0,
        y: position.posY != null ? position.posY : 0,
      },
      data,
      style: { overflow: 'visible' },
    };
  }).filter(Boolean);
}

export function toFlowEdges(edges) {
  return (Array.isArray(edges) ? edges : []).map((edge, index) => {
    if (!edge || typeof edge !== 'object') return null;
    const source = edge.source || edge.sourceNodeId || '';
    const target = edge.target || edge.targetNodeId || '';
    if (!source || !target) return null;
    return {
      id: edge.id || ('edge_' + (index + 1)),
      source,
      target,
      sourceHandle: edge.sourceHandle || HANDLE_OUT,
      type: 'deletable',
    };
  }).filter(Boolean);
}

function edgeEnds(edge) {
  return {
    sourceNodeId: edge && (edge.sourceNodeId || edge.source),
    targetNodeId: edge && (edge.targetNodeId || edge.target),
    sourceHandle: (edge && edge.sourceHandle) || HANDLE_OUT,
  };
}

function sanitizeEdges(edges) {
  return (Array.isArray(edges) ? edges : []).map((edge, index) => {
    if (!edge || typeof edge !== 'object') return null;
    const ends = edgeEnds(edge);
    if (!ends.sourceNodeId || !ends.targetNodeId) return null;
    return {
      id: edge.id || ('edge_' + (index + 1)),
      sourceNodeId: ends.sourceNodeId,
      sourceHandle: ends.sourceHandle,
      targetNodeId: ends.targetNodeId,
    };
  }).filter(Boolean);
}

function persistEdges(edges) {
  return sanitizeEdges(edges);
}

/** Action ids reachable from the trigger by following out-edges, in chain order. */
function connectedActionIds(nodes, edges) {
  const list = nodes || [];
  const trigger = list.find(node => node && node.type === NODE_TYPE_TRIGGER);
  const nextBySource = {};
  (edges || []).forEach(edge => {
    const ends = edgeEnds(edge);
    if (!ends.sourceNodeId || !ends.targetNodeId || nextBySource[ends.sourceNodeId]) return;
    nextBySource[ends.sourceNodeId] = ends.targetNodeId;
  });
  const ordered = [];
  const seen = {};
  let currentId = trigger && trigger.id;
  const actions = list.filter(node => node && node.type === NODE_TYPE_ACTION);
  let steps = 0;
  while (currentId && steps <= actions.length) {
    const nextId = nextBySource[currentId];
    if (!nextId || seen[nextId]) break;
    const next = list.find(node => node && node.id === nextId);
    if (!next || next.type !== NODE_TYPE_ACTION) break;
    seen[nextId] = true;
    ordered.push(nextId);
    currentId = nextId;
    steps += 1;
  }
  return ordered;
}

function actionIdsInOrder(nodes, edges) {
  const ordered = connectedActionIds(nodes, edges);
  const seen = {};
  ordered.forEach(id => { seen[id] = true; });
  (nodes || []).filter(node => node && node.type === NODE_TYPE_ACTION).forEach(node => {
    if (!seen[node.id]) ordered.push(node.id);
  });
  return ordered;
}

/** Action nodes not reachable from the trigger (a rule with these cannot be started from the visual builder). */
export function unconnectedActionIds(graph) {
  const nodes = (graph && graph.nodes) || [];
  const seen = {};
  connectedActionIds(nodes, graph && graph.edges).forEach(id => { seen[id] = true; });
  return nodes.filter(node => node && node.type === NODE_TYPE_ACTION && !seen[node.id]).map(node => node.id);
}

function withTriggerFields(nodes, filtersOp) {
  let updated = false;
  return (nodes || []).map(node => {
    if (updated || !node || node.type !== NODE_TYPE_TRIGGER) return node;
    updated = true;
    const data = { ...(node.data || {}) };
    const filters = Array.isArray(data.filters) ? data.filters : [];
    return {
      ...node,
      data: {
        ...data,
        filtersOp,
        filters: withRowOrders(filters),
      },
    };
  });
}

function withActionOrders(nodes, edges) {
  const orderById = {};
  actionIdsInOrder(nodes, edges).forEach((id, index) => {
    orderById[id] = rowOrder(index);
  });
  return (nodes || []).map(node => {
    if (!node || node.type !== NODE_TYPE_ACTION || !orderById[node.id]) return node;
    return {
      ...node,
      data: { ...(node.data || {}), order: orderById[node.id] },
    };
  });
}

function withStopFields(nodes, stopsOp, exitConditions) {
  let updated = false;
  const next = (nodes || []).map(node => {
    if (updated || !node || node.type !== NODE_TYPE_STOP) return node;
    updated = true;
    const data = { ...(node.data || {}) };
    data.stopsOp = stopsOp;
    if (Array.isArray(exitConditions)) data.exitConditions = copyRows(exitConditions);
    return { ...node, data };
  });
  if (updated) return next;
  return next.concat({
    id: nextPrefixedId(next, STOP_ID_PREFIX),
    type: NODE_TYPE_STOP,
    position: { posX: 0, posY: 0 },
    data: {
      stopsOp,
      exitConditions: Array.isArray(exitConditions) ? copyRows(exitConditions) : [],
    },
  });
}

export function applyBotJsonFields(graph, options) {
  const opts = options || {};
  const filtersOp = opValue(opts.filtersOp);
  const stopsOp = opValue(opts.stopsOp);
  const base = graph || {};
  let nodes = withTriggerFields(base.nodes, filtersOp);
  nodes = withActionOrders(nodes, base.edges);
  nodes = withStopFields(nodes, stopsOp, opts.exitConditions);
  return { ...base, nodes };
}

export function addEdge(graph, sourceNodeId, targetNodeId, sourceHandle) {
  if (!graph || !sourceNodeId || !targetNodeId || sourceNodeId === targetNodeId) return graph;
  const nodes = graph.nodes || [];
  const source = nodes.find(node => node && node.id === sourceNodeId);
  const target = nodes.find(node => node && node.id === targetNodeId);
  if (!source || !target || target.type === NODE_TYPE_TRIGGER) return graph;
  if (source.type === NODE_TYPE_STOP || target.type === NODE_TYPE_STOP) return graph;
  const handle = sourceHandle || HANDLE_OUT;
  const edges = graph.edges || [];
  const duplicate = edges.some(edge => {
    const ends = edgeEnds(edge);
    return ends.sourceNodeId === sourceNodeId && ends.sourceHandle === handle;
  });
  if (duplicate) return graph;
  return {
    ...graph,
    edges: edges.concat({
      id: nextPrefixedId(edges, 'edge_'),
      sourceNodeId,
      sourceHandle: handle,
      targetNodeId,
    }),
  };
}

export function removeEdge(graph, edgeId) {
  if (!graph || !edgeId) return graph;
  return {
    ...graph,
    edges: (graph.edges || []).filter(edge => edge && edge.id !== edgeId),
  };
}

export function updateNodePosition(graph, nodeId, posX, posY) {
  if (!graph || !nodeId) return graph;
  const nodes = graph.nodes || [];
  const node = nodes.find(item => item && item.id === nodeId);
  if (!node) return graph;
  const prevX = node.position && node.position.posX;
  const prevY = node.position && node.position.posY;
  if (prevX === posX && prevY === posY) return graph;
  return {
    ...graph,
    nodes: nodes.map(item => (
      item && item.id === nodeId
        ? { ...item, position: { posX, posY } }
        : item
    )),
  };
}

export function autoLayoutAutomation(graph, measuredNodes) {
  const nodes = (graph && graph.nodes) || [];
  const trigger = nodes.find(node => node && node.type === NODE_TYPE_TRIGGER);
  const visibleNodes = nodes.filter(node => node && node.type !== NODE_TYPE_STOP);
  const laidOut = autoLayoutNodes(visibleNodes, graph && graph.edges, measuredNodes, trigger && trigger.id);
  if (laidOut === visibleNodes) return graph;
  const laidOutById = {};
  laidOut.forEach(node => { laidOutById[node.id] = node; });
  return {
    ...graph,
    nodes: nodes.map(node => (node && laidOutById[node.id]) || node),
  };
}

export function addActionNode(graph, data) {
  const nodes = (graph && graph.nodes) || [];
  let maxX = 80;
  nodes.forEach(node => {
    if (!node || node.type === NODE_TYPE_STOP) return;
    const x = node.position && node.position.posX;
    if (x != null && x > maxX) maxX = x;
  });
  return {
    ...(graph || {}),
    nodes: nodes.concat({
      id: uniqueNodeId(nodes, ACTION_ID_PREFIX),
      type: NODE_TYPE_ACTION,
      position: nodes.length ? { posX: maxX + 280, posY: 80 } : { posX: 360, posY: 80 },
      data: data || {},
    }),
  };
}

export function addConnectedActionNode(graph, sourceNodeId, sourceHandle, data, position) {
  if (!graph || !sourceNodeId) return graph;
  const nodes = graph.nodes || [];
  const source = nodes.find(node => node && node.id === sourceNodeId);
  if (!source || source.type === NODE_TYPE_STOP) return graph;
  const handle = sourceHandle || HANDLE_OUT;
  const taken = (graph.edges || []).some(edge => {
    const ends = edgeEnds(edge);
    return ends.sourceNodeId === sourceNodeId && ends.sourceHandle === handle;
  });
  if (taken) return graph;
  const posX = position && position.posX != null ? position.posX : 360;
  const posY = position && position.posY != null ? position.posY : 80;
  const withNode = {
    ...graph,
    nodes: nodes.concat({
      id: uniqueNodeId(nodes, ACTION_ID_PREFIX),
      type: NODE_TYPE_ACTION,
      position: { posX, posY },
      data: data || {},
    }),
  };
  const created = withNode.nodes[withNode.nodes.length - 1];
  return addEdge(withNode, sourceNodeId, created.id, handle);
}

export function removeNode(graph, nodeId) {
  if (!graph || !nodeId) return graph;
  const nodes = graph.nodes || [];
  const node = nodes.find(item => item && item.id === nodeId);
  if (!node || node.type === NODE_TYPE_TRIGGER || node.type === NODE_TYPE_STOP) return graph;
  const nextNodes = nodes.filter(item => item && item.id !== nodeId);
  if (!nextNodes.some(item => item && item.type === NODE_TYPE_TRIGGER)) return graph;
  const nextEdges = (graph.edges || []).filter(edge => {
    const ends = edgeEnds(edge);
    return ends.sourceNodeId !== nodeId && ends.targetNodeId !== nodeId;
  });
  return { ...graph, nodes: nextNodes, edges: nextEdges };
}

export function duplicateNode(graph, nodeId) {
  if (!graph || !nodeId) return graph;
  const nodes = graph.nodes || [];
  const node = nodes.find(item => item && item.id === nodeId);
  if (!node || node.type === NODE_TYPE_TRIGGER || node.type === NODE_TYPE_STOP) return graph;
  let data = {};
  try {
    data = JSON.parse(JSON.stringify(node.data || {}));
  } catch (e) {
    data = { ...(node.data || {}) };
  }
  delete data.isStartNode;
  delete data.eventLabel;
  delete data.filterLines;
  delete data.actionLabel;
  delete data.detailLine;
  delete data.scheduleLine;
  delete data.headline;
  delete data.lines;
  delete data.onDelete;
  delete data.onDuplicate;
  const posX = node.position && node.position.posX != null ? node.position.posX : 0;
  const posY = node.position && node.position.posY != null ? node.position.posY : 0;
  return {
    ...graph,
    nodes: nodes.concat({
      id: uniqueNodeId(nodes, ACTION_ID_PREFIX),
      type: node.type,
      position: { posX: posX + 40, posY: posY + 40 },
      data,
    }),
  };
}
